import { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Close Icon
const CloseIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function NewsletterPopup({ isOpen, onClose, source = 'popup' }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) return;
    
    setStatus('loading');
    
    try {
      const response = await axios.post(`${API}/api/newsletter/subscribe`, {
        email,
        source
      });
      
      setStatus('success');
      setMessage(response.data?.message === 'Already subscribed' ? 'You\'re already subscribed.' : 'Thank you for subscribing.');
      setEmail('');
      
      // Close popup after 3 seconds on success
      setTimeout(() => {
        onClose();
        setStatus('idle');
        setMessage('');
      }, 3000);
    } catch (error) {
      if (error.response?.data?.message === 'Already subscribed') {
        setStatus('success');
        setMessage('You\'re already subscribed.');
        setTimeout(() => {
          onClose();
          setStatus('idle');
          setMessage('');
        }, 3000);
      } else {
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
        
        setTimeout(() => {
          setStatus('idle');
          setMessage('');
        }, 5000);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[200] flex items-center justify-center"
      data-testid="newsletter-popup-overlay"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Popup Modal */}
      <div 
        className="relative bg-white w-full max-w-md mx-4 p-8 sm:p-10 shadow-2xl"
        style={{ 
          animation: 'fadeInUp 0.3s ease-out'
        }}
        data-testid="newsletter-popup-modal"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:opacity-60 transition-opacity"
          aria-label="Close"
          data-testid="newsletter-popup-close"
        >
          <CloseIcon className="w-5 h-5" style={{ color: '#0f1d2f' }} />
        </button>

        {/* Content */}
        <div className="text-center">
          <p 
            className="text-xs tracking-widest uppercase opacity-40 mb-3" 
            style={{ color: '#0f1d2f', fontFamily: "Arial, sans-serif" }}
          >
            Stay Connected
          </p>
          <h3 
            className="text-2xl sm:text-3xl mb-4" 
            style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}
          >
            Join Our Community
          </h3>
          <p 
            className="text-sm sm:text-base leading-relaxed mb-8 max-w-sm mx-auto" 
            style={{ color: '#0f1d2f', opacity: 0.7 }}
          >
            Receive updates on new farms, seasonal offerings, and stories from the land.
          </p>
          
          {status === 'success' ? (
            <div className="py-4">
              <p 
                className="text-lg" 
                style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}
              >
                {message}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="w-full px-4 py-4 border text-sm focus:outline-none focus:border-[#0f1d2f] transition-colors text-center"
                style={{ 
                  borderColor: 'rgba(30, 58, 95, 0.2)', 
                  color: '#0f1d2f',
                  fontFamily: "Arial, sans-serif"
                }}
                disabled={status === 'loading'}
                data-testid="newsletter-popup-email"
              />
              <button
                type="submit"
                disabled={status === 'loading' || !email}
                className="w-full px-8 py-4 bg-[#0f1d2f] text-white text-xs tracking-widest uppercase hover:bg-[#0f1d2f]/90 transition-all disabled:opacity-50"
                data-testid="newsletter-popup-submit"
              >
                {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          )}
          
          {status === 'error' && (
            <p className="text-sm text-red-600 mt-4">{message}</p>
          )}
        </div>
      </div>

      {/* Animation keyframes */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
